import queryString from "query-string";
import React from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";
import { getSettings } from "src/services/configService";
import { PermissionService } from "src/services/permissionService/permissionService";
import { useDispatch } from "src/store";
import { useAnalyticsClick } from "src/ui/hooks/useAnalyticsClick";
import { makeStyles } from "tss-react/mui";

import { DropdownItem } from "@dashboard/devices/components/shared";
import { BatchActionState, setBatchMode } from "@dashboard/devices/store";
import CreateGroupDialog from "@dashboard/groups/components/CreateGroupDialog";
import { useCurrentSubscriptionExpired, useCurrentWorkspace } from "@dashboard/workspaces/api/helpers";

import DropdownContainer from "../shared/Dropdown/DropdownContainer";
import { AddDeviceModal } from "../Table/AddDeviceModal/AddDeviceModal";

import ExportCSV from "./ExportCSV";

const useStyles = makeStyles()((theme) => ({
    manageIcon: {
        fontSize: theme.spacing(4),
    },
}));

type SelectBatchActionMode = (entity: BatchActionState["entity"], type: BatchActionState["type"]) => void;

type Props = {
    anchorRef: React.RefObject<HTMLButtonElement | null>;
    onSelectColumns: () => void;
    onResetColumns: () => void;
};

export const Menu = ({ anchorRef, onSelectColumns, onResetColumns }: Props) => {
    const [isCreateGroupDialogOpen, setCreateGroupDialogOpen] = React.useState(false);
    const [addDeviceModalChoice, setAddDeviceModalChoice] = React.useState<boolean | null>(null);
    const trackClick = useAnalyticsClick();
    const { classes, cx } = useStyles();
    const dispatch = useDispatch();
    const { t } = useTranslation(["pages", "common"]);
    const { data: workspace } = useCurrentWorkspace();
    const isExpired = useCurrentSubscriptionExpired();
    const location = useLocation();
    const navigate = useNavigate();

    const { groupAbility } = PermissionService();
    const { features } = getSettings();

    const searchParams = queryString.parse(location.search);
    const addDeviceModalOpen = addDeviceModalChoice ?? searchParams.openEnrollmentModal === "true";

    const closeAddDeviceModal = () => {
        setAddDeviceModalChoice(false);
        if (searchParams.openEnrollmentModal !== undefined) {
            const newSearch = queryString.stringify(
                { ...searchParams, openEnrollmentModal: undefined },
                { skipEmptyString: true, skipNull: true },
            );
            void navigate({ search: newSearch }, { replace: true });
        }
    };

    const selectBatchActionMode: SelectBatchActionMode = (entity, type) => {
        dispatch(setBatchMode({ type, entity }));
    };

    const getTooltipTitle = () => {
        if (isExpired) {
            return t("pages:devicePage.tooltip.subscriptionExpired");
        }
        if (groupAbility(null, workspace).cannot("create", "Group")) {
            return t("pages:devicePage.tooltip.yourRoleDoesNotAllowYouToCreateGroups");
        }
        return "";
    };

    const handleAddDevice = () => {
        if (features.enrollmentFlowEnabled) {
            navigate("/dashboard/enrollment");
            return;
        }
        setAddDeviceModalChoice(true);
    };

    return (
        <React.Fragment>
            <DropdownContainer
                label={t("common:button.manage")}
                icon={<i className={cx("fa-solid fa-gear", classes.manageIcon)} />}
                anchorRef={anchorRef}
            >
                <DropdownItem
                    onClick={handleAddDevice}
                    label={t("pages:devicePage.button.addDevice")}
                    iconName="fa-solid fa-plus"
                />
                <DropdownItem
                    label={t("pages:devicePage.button.addGroup")}
                    iconName="fa-solid fa-folder-tree"
                    onClick={trackClick(() => setCreateGroupDialogOpen(true), "add-group", "dropdown")}
                    disabled={groupAbility(null, workspace).cannot("create", "Group")}
                    tooltipProps={{
                        title: getTooltipTitle(),
                        hide: groupAbility(null, workspace).can("create", "Group"),
                    }}
                />

                <DropdownItem
                    label={t("pages:devicePage.button.moveLicenses")}
                    iconName="fa-solid fa-up-down-left-right"
                    onClick={trackClick(() => selectBatchActionMode("license", "move"), "move-licenses", "dropdown")}
                />
                <ExportCSV />

                <DropdownItem
                    onClick={trackClick(onSelectColumns, "select-columns", "dropdown")}
                    label={t("pages:devicePage.button.selectColumns")}
                    iconName="fa-solid fa-list-check"
                />
                <DropdownItem
                    onClick={trackClick(onResetColumns, "reset-columns", "dropdown")}
                    label={t("pages:devicePage.button.resetColumns")}
                    iconName="fa-solid fa-arrows-rotate"
                />
            </DropdownContainer>

            <AddDeviceModal open={addDeviceModalOpen} onClose={closeAddDeviceModal} />
            <CreateGroupDialog open={isCreateGroupDialogOpen} onClose={() => setCreateGroupDialogOpen(false)} />
        </React.Fragment>
    );
};
